// Materiales line discounts - outlet model (CLAUDE.md §8.7, PO 2026-09-23).
// Shared by BudgetFormNew and BudgetDetailNew so the on-screen math matches
// the server's (`BudgetService._compute_section_amounts`).
//
//  - Outlet line: `descuento_linea` starts at the captured product discount
//    (`descuento_producto`) and may only go down. Never gets the ambiente %.
//  - Non-outlet line (in-situ included): `descuento_linea` comes from the
//    ambiente's "Aplicar descuento" % (`descuento_materiales`, percentage
//    points), each line then editable individually.
// All discounts on a line are fractions (0.10 == 10%).

interface DiscountLine {
  es_in_situ?: boolean;
  product?: { is_outlet?: boolean } | null;
  descuento_producto?: number;
  descuento_linea?: number;
}

export const isOutletLine = (m: DiscountLine): boolean =>
  !m.es_in_situ && !!m.product?.is_outlet;

/** The applied discount a brand-new line starts with. */
export const initialLineDiscount = (m: DiscountLine, ambientePct: number): number =>
  isOutletLine(m) ? (m.descuento_producto || 0) : (ambientePct || 0) / 100;

/** Clamp a typed % (0-100 points) to what the line allows, as a fraction. */
export const clampLineDiscount = (m: DiscountLine, pct: number): number => {
  const max = isOutletLine(m) ? (m.descuento_producto || 0) : 0.9999;
  return Math.min(Math.max((pct || 0) / 100, 0), max);
};

/** Ticking/changing the ambiente % overwrites every non-outlet line's
 *  discount; unticking (pct 0) clears them. Outlet lines are untouched. */
export const applyAmbienteDiscount = <M extends DiscountLine, E extends { descuento_materiales?: number; materials: M[] }>(
  env: E, pct: number,
): E => ({
  ...env,
  descuento_materiales: pct,
  materials: env.materials.map(m => (isOutletLine(m) ? m : { ...m, descuento_linea: pct / 100 })),
});

export const lineDiscountAmount = (monto: number, m: DiscountLine): number =>
  monto * (m.descuento_linea || 0);
